import { useEffect, useState } from "react";
import { api, errorMsg } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { CalendarClock, ArrowRight, AlertTriangle, HelpCircle, CheckCircle2 } from "lucide-react";

const Chip = ({ label, valor, color }) => (
  <div className={`rounded-lg px-3 py-2 ${color}`}>
    <div className="text-lg font-bold leading-none">{valor}</div>
    <div className="text-[11px] uppercase tracking-wide mt-1 opacity-80">{label}</div>
  </div>
);

export function AsignacionAnioDialog({ abierto, onCerrar, onAplicado }) {
  const [cargando, setCargando] = useState(false);
  const [aplicando, setAplicando] = useState(false);
  const [data, setData] = useState(null);
  const [excluir, setExcluir] = useState(new Set());

  const cargar = () => {
    setCargando(true);
    setExcluir(new Set());
    api.get("/mantenimiento/asignacion-anio/preview")
      .then((r) => setData(r.data))
      .catch((e) => toast.error(errorMsg(e)))
      .finally(() => setCargando(false));
  };

  useEffect(() => {
    if (!abierto) { setData(null); return; }
    cargar();
  }, [abierto]);

  const toggleExcluir = (id) => setExcluir((prev) => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  const aplicar = async () => {
    const totalAplicar = (data?.resumen?.reasignar || 0) - excluir.size;
    if (!window.confirm(`¿Aplicar ${totalAplicar} reasignación(es)? Los casos "sin rango" y "ambiguos" no se tocan.`)) return;
    setAplicando(true);
    try {
      const { data: res } = await api.post("/mantenimiento/asignacion-anio/aplicar", { excluir_ids: [...excluir] });
      toast.success(`${res.reasignados} vehículo(s) reasignado(s)${res.excluidos ? `, ${res.excluidos} excluido(s)` : ""}`);
      onAplicado?.();
      cargar();
    } catch (e) { toast.error(errorMsg(e)); }
    finally { setAplicando(false); }
  };

  const r = data?.resumen;
  const totalAplicar = (r?.reasignar || 0) - excluir.size;

  return (
    <Dialog open={abierto} onOpenChange={(o) => { if (!o) onCerrar?.(); }}>
      <DialogContent data-testid="asignacion-anio-dialog" className="max-w-3xl max-h-[92vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarClock size={18} /> Revisar asignación por año
          </DialogTitle>
          <DialogDescription>
            Compara el año de cada vehículo con el rango de años de su modelo (mismo nombre base) y propone
            corregir la asignación. Nada cambia hasta confirmar con "Corregir todo".
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4 pr-1">
          {cargando && <p className="text-sm text-slate-400 py-8 text-center" data-testid="asignacion-anio-cargando">Analizando…</p>}

          {!cargando && r && (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2" data-testid="asignacion-anio-resumen">
                <Chip label="Revisados" valor={r.revisados} color="bg-slate-100 text-slate-700" />
                <Chip label="OK" valor={r.ok} color="bg-emerald-50 text-emerald-700" />
                <Chip label="A reasignar" valor={r.reasignar} color="bg-indigo-50 text-indigo-700" />
                <Chip label="Sin rango" valor={r.sin_rango} color="bg-amber-50 text-amber-700" />
                <Chip label="Ambiguos" valor={r.ambiguo} color="bg-rose-50 text-rose-700" />
              </div>

              {r.reasignar === 0 && r.sin_rango === 0 && r.ambiguo === 0 && (
                <p data-testid="asignacion-anio-todo-ok" className="text-sm text-emerald-700 bg-emerald-50 rounded-lg px-4 py-3 flex items-center gap-2">
                  <CheckCircle2 size={16} /> Todo está correctamente asignado. No hay cambios que proponer.
                </p>
              )}

              {data.por_grupo.length > 0 && (
                <div className="space-y-3">
                  <h3 className="text-xs font-semibold uppercase text-slate-500">Reasignaciones propuestas</h3>
                  {data.por_grupo.map((g, gi) => (
                    <div key={gi} data-testid={`asignacion-grupo-${gi}`} className="border border-slate-200 rounded-lg p-3">
                      <div className="text-sm font-semibold text-slate-700 mb-2">{g.grupo.replace("|", " ")}</div>
                      <div className="space-y-2">
                        {g.transiciones.map((t, ti) => (
                          <div key={ti} className="text-sm bg-slate-50 rounded-md px-3 py-2">
                            <div className="flex items-center gap-2 flex-wrap text-slate-700">
                              <span className="font-medium">{t.desde_nombre}</span>
                              <ArrowRight size={14} className="text-indigo-500" />
                              <span className="font-medium text-indigo-700">{t.hacia_nombre}</span>
                              <span className="text-xs text-slate-400">· {t.cantidad} vehículo(s)</span>
                            </div>
                            <div className="text-xs text-slate-500 mt-1 flex flex-wrap gap-1.5">
                              {t.ejemplos.map((e, ei) => (
                                <span key={ei} className="bg-white border border-slate-200 rounded px-1.5 py-0.5">
                                  {e.placa} ({e.anio})
                                </span>
                              ))}
                              {t.cantidad > t.ejemplos.length && <span>… +{t.cantidad - t.ejemplos.length}</span>}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {r.reasignar > 0 && (
                <details className="border border-slate-200 rounded-lg" data-testid="asignacion-excluir">
                  <summary className="cursor-pointer px-3 py-2 text-xs font-semibold uppercase text-slate-500">
                    Excluir casos puntuales ({excluir.size} excluido(s))
                  </summary>
                  <div className="max-h-56 overflow-y-auto divide-y divide-slate-100">
                    {data.reasignaciones.map((x) => (
                      <label key={x.vehiculo_id} className="flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-slate-50 cursor-pointer">
                        <input type="checkbox" data-testid={`asignacion-excluir-${x.vehiculo_id}`}
                          checked={excluir.has(x.vehiculo_id)} onChange={() => toggleExcluir(x.vehiculo_id)}
                          className="accent-rose-500 w-4 h-4" />
                        <span className="text-slate-700">{x.placa} ({x.anio})</span>
                        <span className="text-xs text-slate-400">{x.desde_nombre} → {x.hacia_nombre}</span>
                      </label>
                    ))}
                  </div>
                </details>
              )}

              {data.sin_rango.length > 0 && (
                <div className="border border-amber-200 rounded-lg p-3" data-testid="asignacion-sin-rango">
                  <h3 className="text-xs font-semibold uppercase text-amber-700 flex items-center gap-1.5 mb-2">
                    <AlertTriangle size={14} /> Sin rango — revisión manual ({data.sin_rango.length})
                  </h3>
                  <div className="max-h-40 overflow-y-auto text-sm space-y-1">
                    {data.sin_rango.map((x) => (
                      <div key={x.vehiculo_id} className="text-slate-600">
                        {x.placa} ({x.anio}) — hoy: <span className="text-slate-800">{x.modelo_nombre}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {data.ambiguos.length > 0 && (
                <div className="border border-rose-200 rounded-lg p-3" data-testid="asignacion-ambiguos">
                  <h3 className="text-xs font-semibold uppercase text-rose-700 flex items-center gap-1.5 mb-2">
                    <HelpCircle size={14} /> Ambiguos (solapamiento) — revisión manual ({data.ambiguos.length})
                  </h3>
                  <div className="max-h-40 overflow-y-auto text-sm space-y-1">
                    {data.ambiguos.map((x) => (
                      <div key={x.vehiculo_id} className="text-slate-600">
                        {x.placa} ({x.anio}) — hoy: <span className="text-slate-800">{x.modelo_nombre}</span>
                        <span className="text-xs text-rose-500"> · encaja en: {x.candidatos.map((c) => c.nombre).join(", ")}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {data.modelos_sin_vehiculos.length > 0 && (
                <div className="text-xs text-slate-500 bg-slate-50 rounded-lg px-3 py-2" data-testid="asignacion-sin-vehiculos">
                  <strong>Aviso:</strong> {data.modelos_sin_vehiculos.length} modelo(s) quedarían sin vehículos tras aplicar:{" "}
                  {data.modelos_sin_vehiculos.map((m) => m.nombre).join(", ")}. No se borra nada automáticamente.
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-3 border-t border-slate-100 shrink-0">
          <Button variant="outline" data-testid="asignacion-anio-cerrar-btn"
            className="border-teal-500 text-teal-600 hover:bg-teal-50" onClick={() => onCerrar?.()}>Cerrar</Button>
          <Button data-testid="asignacion-anio-aplicar-btn" onClick={aplicar}
            disabled={aplicando || cargando || !r || totalAplicar <= 0}
            className="bg-indigo-600 hover:bg-indigo-700 text-white disabled:opacity-40">
            {aplicando ? "Aplicando…" : `Corregir todo${totalAplicar > 0 ? ` (${totalAplicar})` : ""}`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
